import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Code, Globe, Zap, Settings, ShieldCheck, Database, Smartphone, Cpu } from "lucide-react";
import "../styles/howTos.css";

const qaCategories = [
  {
    id: "manual-testing",
    title: "Manual Testing Essentials",
    description: "Master the fundamentals of software testing, test cases, and bug lifecycle.",
    icon: <ClipboardCheck size={32} className="card-icon blue" />,
    count: "50+ Questions",
    tag: "Fundamentals"
  },
  {
    id: "automation-testing",
    title: "Automation Testing",
    description: "Deep dive into Selenium, Cypress, frameworks, and coding concepts.",
    icon: <Code size={32} className="card-icon purple" />,
    count: "50+ Questions",
    tag: "Coding"
  },
  {
    id: "api-testing",
    title: "API & Web Services",
    description: "Crack Postman, REST Assured, HTTP methods, and status codes.",
    icon: <Globe size={32} className="card-icon green" />,
    count: "95+ Questions",
    tag: "Backend"
  },
  {
    id: "performance-testing",
    title: "Performance Testing",
    description: "Learn JMeter, LoadRunner, stress testing, and scalability.",
    icon: <Zap size={32} className="card-icon orange" />,
    count: "50+ Questions",
    tag: "Performance"
  },
  {
    id: "agile-qa",
    title: "Agile & DevOps for QA",
    description: "Understand Scrum, Jira, CI/CD pipelines, and QA integration.",
    icon: <Settings size={32} className="card-icon magenta" />,
    count: "50+ Questions",
    tag: "Process"
  },
  {
    id: "security-testing",
    title: "Security Testing",
    description: "Explore OWASP Top 10, vulnerability scanning, and pen testing basics.",
    icon: <ShieldCheck size={32} className="card-icon red" />,
    count: "50+ Questions",
    tag: "Security"
  },
  {
    id: "sql-qa",
    title: "SQL Database & Queries",
    description: "Prepare with essential relational database concepts, joins, subqueries, and ACID properties.",
    icon: <Database size={32} className="card-icon blue" />,
    count: "52+ Questions",
    tag: "Database"
  },
  {
    id: "playwright-qa",
    title: "Playwright Automation",
    description: "Master modern web testing with Playwright, custom fixtures, network interception, and auto-waiting.",
    icon: <Code size={32} className="card-icon green" />,
    count: "50+ Questions",
    tag: "Automation"
  },
  {
    id: "selenium-qa",
    title: "Selenium Automation with Java",
    description: "Prepare with essential Selenium concepts, waits, Page Object Model, and custom browser capabilities.",
    icon: <ClipboardCheck size={32} className="card-icon purple" />,
    count: "50+ Questions",
    tag: "Automation"
  },
  {
    id: "appium-qa",
    title: "Appium Mobile Testing",
    description: "Learn mobile automation for Android and iOS using Appium, capabilities, and gestures.",
    icon: <Smartphone size={32} className="card-icon red" />,
    count: "50+ Questions",
    tag: "Automation"
  },
  {
    id: "git-commands",
    title: "Git Commands",
    description: "A quick reference guide for commonly used Git commands for daily use and interviews.",
    icon: <Code size={32} className="card-icon blue" />,
    count: "13 Categories",
    tag: "Process"
  },
  {
    id: "python-course",
    title: "Python Course",
    description: "Learn Python programming from scratch with explanations and examples.",
    icon: <Code size={32} className="card-icon purple" />,
    count: "35 Lessons",
    tag: "Coding"
  },
  {
    id: "javascript-course",
    title: "JavaScript Course",
    description: "Master modern JavaScript from basic variables to advanced Async/Await concepts.",
    icon: <Code size={32} className="card-icon orange" />,
    count: "33 Lessons",
    tag: "Coding"
  },
  {
    id: "javascript-programs",
    title: "JS Programs",
    description: "A collection of basic to advanced JavaScript programs to practice coding.",
    icon: <Code size={32} className="card-icon green" />,
    count: "104 Programs",
    tag: "Coding"
  },
  {
    id: "python-programs",
    title: "Python Programs",
    description: "A collection of basic to advanced Python programs to practice coding.",
    icon: <Code size={32} className="card-icon blue" />,
    count: "24 Programs",
    tag: "Coding"
  },
  {
    id: "dsa-course",
    title: "DSA Course",
    description: "Master foundational Data Structures & Algorithms, Big-O analysis, memory models, and core algorithms.",
    icon: <Database size={32} className="card-icon blue" />,
    count: "25 Lessons",
    tag: "Coding"
  },
  {
    id: "dsa-programs",
    title: "DSA Programs",
    description: "Master essential Data Structures & Algorithms programs with explanations and code examples.",
    icon: <Cpu size={32} className="card-icon purple" />,
    count: "27 Programs",
    tag: "Coding"
  }
];

const tags = ["All", "Fundamentals", "Automation", "Database", "Security", "Backend", "Process", "Performance", "Coding"];

export default function HowTos() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeTag, setActiveTag] = useState("All");

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setAppliedSearch(searchQuery);
  };

  const filteredCategories = qaCategories.filter((cat) => {
    const matchesTag = activeTag === "All" || cat.tag === activeTag;
    const matchesSearch =
      cat.title.toLowerCase().includes(appliedSearch.toLowerCase()) ||
      cat.description.toLowerCase().includes(appliedSearch.toLowerCase()) ||
      cat.tag.toLowerCase().includes(appliedSearch.toLowerCase());
    return matchesTag && matchesSearch;
  });

  return (
    <div className="howtos-container">
      <div className="howtos-header">
        <span className="howtos-badge">QA Interview Guide</span>
        <h1>Crack Your Next <span>QA Interview</span></h1>
        <p>Expert-curated question and answer modules designed specifically for Quality Assurance professionals.</p>
      </div>

      <div className="howtos-filter-container">
        <form onSubmit={handleSearch} className="howtos-search-form">
          <input
            type="text"
            placeholder="Search guides (e.g. Playwright, SQL)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="howtos-search-input"
          />
          <button type="submit" className="howtos-search-button">
            Search
          </button>
        </form>

        <div className="howtos-select-wrapper">
          <select
            id="category-select"
            value={activeTag}
            onChange={(e) => setActiveTag(e.target.value)}
            className="howtos-dropdown-select"
          >
            {tags.map((tag) => (
              <option key={tag} value={tag}>
                {tag === "All" ? "All Categories" : tag}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredCategories.length > 0 ? (
        <div className="howtos-grid">
          {filteredCategories.map((cat) => (
            <div key={cat.id} className="howto-card" onClick={() => navigate(`/how-tos/${cat.id}`)}>
              <div className="howto-card-top">
                <span className="howto-tag">{cat.tag}</span>
                <span className="howto-count">{cat.count}</span>
              </div>
              <div className="howto-card-content">
                <div className="howto-icon-wrap">
                  {cat.icon}
                </div>
                <h3>{cat.title}</h3>
                <p>{cat.description}</p>
              </div>
              <div className="howto-card-footer">
                <span className="howto-btn">Start Preparing &rarr;</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="howtos-no-results">
          <h3>No matching guides found</h3>
          <p>Try searching for other terms or resetting the filter tags.</p>
          <button
            onClick={() => {
              setSearchQuery("");
              setAppliedSearch("");
              setActiveTag("All");
            }}
            className="howto-reset-btn"
          >
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
}
